<template>
  <div class="bottom-bar action-bar">
    <button class="button button-blue" :disabled="!madeChanges || loading" @click="saveHints">Save</button>

    <div>
      <button class="button button-gray" @click="reclassify" :disabled="reclassifying || loading">Reclassify</button>
      <button class="button button-gray" @click="reclassifyAll" :disabled="reclassifying || loading">all</button>
      <Checkbox v-if="site.ocl_version === 4" class="keep-text" id="keep-text" v-model="keepText" label="Keep article text" content="Keep article text when reclassifying" v-tippy/>
    </div>

    <button type="button" class="button button-transparent" @click="showHelp = !showHelp">Help!</button>
    <Modal v-show="showHelp" @close="showHelp = false"
           :tabs="{ help: 'Help', hf: 'Hint Functions', impact: 'Hint Impact', ex: 'Extractor'}">
      <template v-slot:header>OHM Help</template>
      <template v-slot:help>
        <h4>Notes</h4>
        <ul class="list">
          <li>
            For now there is no live input validation, but this will be added in the future. This means input is only
            validated when saving, and if there were any
            errors, the relevant statements and/or arguments will be marked in red. A red "Error" button will also
            appear in the bottom of the screen, clicking it
            allows you to see more detailed information about the errors.
          </li>
          <li>For input fields with autocomplete, you can press Ctrl + Space while the input is empty to get a list of
            all possible values
          </li>
          <li>Hint tabs will get a yellow color when their URL rule matches the currently selected article, and be
            outlined in red if it contains any errors
          </li>
          <li>Hint tabs that contain one or more hint statements will have an ohm symbol (Ω) signifying they are
            non-empty
          </li>
        </ul>
        <h4>Fixing errors</h4>
        <p>
          If there was an error while parsing the LinkFilter hints, the corresponding hint tab will be marked in red.
          Click on it, and the hint statement with the
          error will be marked in red. Hover your mouse over the hint statement, and text will appear containing the
          error message.
        </p>
        <p>If the hint statement has errors, but we were still able to parse its arguments, they will appear after the
          error message.</p>
        <h4>Keyboard shortcuts</h4>
        <table class="ohm-table">
          <thead>
          <tr>
            <th>Keystroke</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>
              <span class="key">Ctrl</span><span class="key">S</span>
            </td>
            <td>Save all hints</td>
          </tr>
          <tr>
            <td>
              <span class="key">Ctrl</span><span class="key">E</span>
            </td>
            <td>Reclassify selected article</td>
          </tr>
          <tr>
            <td>
              <span class="key">Ctrl</span><span class="key">Shift</span><span class="key">E</span>
            </td>
            <td>Reclassify all</td>
          </tr>
          </tbody>
        </table>
      </template>
      <template v-slot:hf>
        <p>
          Below is a complete list of all Hint Functions. The editor will only show functions that are compatible with
          the current site, based on the OCL version it uses.
        </p>
        <p>
          Obsolete functions are <span class="obsolete">grayed</span> out. They still work (probably), but are not
          recommended to use.
        </p>
        <table class="ohm-table">
          <thead>
          <tr>
            <th>OCL versions</th>
            <th>Function</th>
            <th>Description</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="hf in hintFunctions" :key="hf.id" :class="{ obsolete: hf.is_obsolete }">
            <td>{{ hf.ocl_versions }}</td>
            <td class="function-name">
              {{ hf.name }}{{ hf.start_args }}<span v-for="(arg, index) of hf.args"
                                                    :key="hf.id + '-' + arg.name"><span class="arg"
                                                                                        :content="buildArgTitle(arg)"
                                                                                        v-tippy>{{ arg.name }}</span><template
              v-if="index !== (Object.keys(hf.args).length - 1)">, </template></span>
              <template v-if="hf.group !== 'python'">{{ hf.end_args }}</template>
            </td>
            <td class="function-description">{{ hf.description }}</td>
          </tr>
          </tbody>
        </table>
      </template>
      <template v-slot:impact>
        <h4>Hint Impact</h4>
        <p>
          Hint Impact is a way of measuring how much impact a hint statement has on an article. The unit measured is
          characters changed. It's represented as two percentages <i>real</i> and <i>potential</i> impact, meaning what
          percentage of the article changed after the hint was applied.
        </p>
      </template>
      <template v-slot:ex>
        <h4>Video Extractor</h4>
        <p>
          ...
        </p>
      </template>

    </Modal>

    <button type="button" class="button button-red" v-show="errors && errors.length"
            @click="showErrors = !showErrors">Errors
    </button>
    <Modal v-show="showErrors && errors" @close="showErrors = false">
      <template v-slot:header>Errors</template>
      <slot>
        <div v-for="(error, i) in errors" :key="i">
          {{ error }}
        </div>
      </slot>
    </Modal>

    <ul class="links">
      <li>
        <EditorLink v-if="selectedArticle" target="_blank"
                    :href="`https://tools${urlDev}/AI/art_info.php?id_link=${selectedArticle.id_link}`">
          Article Info
          <SvgIcon icon="external-url" :size="10"/>
        </EditorLink>
      </li>
      <li>
        <EditorLink
          v-if="selectedArticle"
          :href="`/api/classified/${selectedArticle.id_link}?highlight=false`"
          target="_blank">
          Original page
          <SvgIcon icon="external-url" :size="10"/>
        </EditorLink>
      </li>
      <li>
        <EditorLink v-if="site" target="_blank"
                    :href="`https://tools${urlDev}/sources/index.php?search_expr=site%3D${site.id_site}&search_mode=site`">
          Source Search
          <SvgIcon icon="external-url" :size="10"/>
        </EditorLink>
      </li>
    </ul>

    <button type="button" class="button toggle-btn" :class="{ 'focused-on': focused }"
            @click="$store.commit('editor/setFocused', !focused)">
      Minimize UI
    </button>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Events } from '../../events'

import { RepositoryFactory } from '../../data/RepositoryFactory'
import Modal from '../Modal'
import SvgIcon from '../SvgIcon'
import EditorLink from './EditorLink'
import Checkbox from '../form/Checkbox'

const OHMRepository = RepositoryFactory.get('ohm')

export default {
  name: 'EditorActionBar',
  components: { EditorLink, Modal, SvgIcon, Checkbox },
  data: () => ({
    // Modal states
    showHelp: false,
    showErrors: false,

    keepText: false,
  }),
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    saving: {
      type: Boolean,
      default: false
    },
    madeChanges: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    urlDev: String,
  },
  mounted() {
    // TODO: move keybindings from Editor.vue to here
  },
  beforeDestroy() {
    // TODO: unregister keybindings...?
  },
  methods: {
    saveHints() {
      this.$emit('save')
    },
    reclassifyAll() {
      if (this.reclassifying || this.loading || this.saving) {
        return
      }
      this.reclassifying = true

      Events.$emit('addNotification', {
        type: 'info',
        msg: 'Reclassifying all started',
        timeout: 3
      })

      Events.$emit('setStatusMessage', `Reclassifying 0/${this.articles.length}...`)
      // this.$store.state.statusText = `reclassifying 0/${this.articles.length}...`

      let progress = 0
      let finished = 0
      for (let i = 0; i < this.articles.length; i++) {

        OHMRepository.reclassify(this.site.id_site, this.articles[i].id_link, this.keepText)
        .then(response => {
          if (response.status === 200) {
            finished += 1
            // this.$store.state.statusText = 'reclassifying ' + finished + '/' + this.articles.length + '...'
            Events.$emit('setStatusMessage', `Reclassifying ${finished}/${this.articles.length}...`)
          } else if (response.data) {
            Events.$emit('addNotification', {
              type: response.data.type || 'error',
              msg: response.data.message || 'Error occurred while reclassifying ' + this.articles[i].id_link,
              timeout: 5
            })
          } else {
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'Error occurred while reclassifying ' + this.articles[i].id_link,
              timeout: 5
            })
          }
        }).catch(error => {
          if (error.response) {
            Events.$emit('addNotification', {
              type: error.response.type || 'error',
              msg: error.response.message || 'Error occurred while reclassifying ' + this.articles[i].id_link,
              timeout: 5
            })
          } else {
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'Error occurred while reclassifying ' + this.articles[i].id_link,
              timeout: 5
            })
          }
        }).then(() => {
          progress += 1

          if (progress === this.articles.length) {
            Events.$emit('addNotification', {
              type: 'success',
              msg: 'Reclassifying done. ' + finished + '/' + this.articles.length + ' completed',
              timeout: 5
            })

            Events.$emit('reclassified')

            setTimeout(() => {
              // this.$store.state.statusText = ''
              Events.$emit('setStatusMessage', '')
              this.reclassifying = false
            }, 3000)
          }
        })
      }
    },
    reclassify() {
      if (!this.selectedArticle || this.loading || this.saving) {
        return
      }

      Events.$emit('addNotification', {
        type: 'info',
        msg: 'Reclassifying started',
        timeout: 3
      })
      this.reclassifying = true

      // this.$store.state.statusText = 'reclassifying ' + this.selectedArticle.id_link + '...'
      Events.$emit('setStatusMessage', `Reclassifying ${this.selectedArticle.id_link}...`)
      OHMRepository.reclassify(this.id_site, this.selectedArticle.id_link, this.keepText)
      .then(response => {
        if (response.data) {
          Events.$emit('reclassified')
          Events.$emit('addNotification', {
            type: response.data.type || 'success',
            msg: response.data.message || 'Reclassifying done',
            timeout: 3
          })
        } else {
          Events.$emit('addNotification', {
            type: 'error',
            msg: 'Error occurred while reclassifying',
            timeout: 5
          })
        }
      }).catch(error => {
        if (error.response) {
          Events.$emit('addNotification', {
            type: error.response.type || 'error',
            msg: error.response.message || 'Error occurred while reclassifying',
            timeout: 5
          })
        } else {
          Events.$emit('addNotification', {
            type: 'error',
            msg: 'Error occurred while reclassifying',
            timeout: 5
          })
        }
      }).then(() => {
        setTimeout(() => {
          // this.$store.state.statusText = ''
          Events.$emit('setStatusMessage', '')
          this.reclassifying = false
        }, 3000)
      })
    },
    buildArgTitle(arg) {
      let title = 'Type: ' + arg.type

      if ('description' in arg) {
        title += '<br>Description: ' + arg.description
      }

      title += '<br>Required: ' + (arg.required ? 'Yes' : 'No')

      const isListType = (typeof arg.type !== 'string')

      if (arg.type === 'int' || (isListType && 'int' in arg.type)) {
        let minval = null
        let maxval = null
        if ('min_value' in arg) {
          minval = arg.min_value
        }
        if ('max_value' in arg) {
          maxval = arg.max_value
        }

        if (minval != null || maxval != null) {
          title += '<br>Valid int: '

          if (minval != null) {
            title += minval + ' <= '
          }
          title += arg.name
          if (maxval != null) {
            title += ' >= ' + maxval
          }
        }
      } else if (arg.type === 'list') {
        title += '<br>List type: ' + arg.valid_list_type
      }

      if ((arg.type === 'text' || (isListType && 'text' in arg.type) || (arg.type === 'list' && 'valid_list_type' in arg && arg.valid_list_type === 'text')) && 'valid_text' in arg) {
        title += '<br>Valid text: ' + arg.valid_text
      }

      return title
    }
  },
  computed: {
    ...mapState('site', {
      site: state => state.data,
    }),
    ...mapState('hint', {
      hintFunctions: state => state.hintFunctions,
    }),
    ...mapState('article', {
      articles: state => state.latestArticles,
      selectedArticle: state => state.selected,
    }),
    ...mapState('editor', {
      focused: state => state.focused,
      tabPage: state => state.tabPage,
    }),
    reclassifying: {
      set(n) {
        this.$store.commit('setReclassifying', n)
      },
      get() {
        return this.$store.state.reclassifying
      }
    },
  },
}
</script>

<style scoped lang="scss">
@use '../../styles/variables' as v;

.bottom-bar {
  font-size: v.$font-size-small;
}

.keep-text {
  margin: 0 5px;
  position: relative;
  bottom: -2px;
}
</style>
